import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

const getTextFromChildren = (children) =>
  React.Children.toArray(children)
    .filter((child) => typeof child === 'string' || typeof child === 'number')
    .join('');

const findMeta = (attribute, value) =>
  Array.from(document.head.querySelectorAll(`meta[${attribute}]`)).find(
    (meta) => meta.getAttribute(attribute) === value
  );

const findLink = (attribute, value) =>
  Array.from(document.head.querySelectorAll(`link[${attribute}]`)).find(
    (link) => link.getAttribute(attribute) === value
  );

const upsertMeta = ({ content, name, property }, restorers) => {
  const keyAttribute = name ? 'name' : property ? 'property' : null;
  const keyValue = name || property;
  if (!keyAttribute || !keyValue) return;

  let element = findMeta(keyAttribute, keyValue);
  const created = !element;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(keyAttribute, keyValue);
    document.head.appendChild(element);
  }

  const previousContent = element.getAttribute('content');
  if (typeof content === 'string') {
    element.setAttribute('content', content);
  }

  restorers.push(() => {
    if (created) {
      element.remove();
      return;
    }

    if (previousContent === null) {
      element.removeAttribute('content');
      return;
    }

    element.setAttribute('content', previousContent);
  });
};

const upsertCanonicalLink = ({ href, rel }, restorers) => {
  if (rel !== 'canonical' || !href) return;

  let element = findLink('rel', 'canonical');
  const created = !element;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  const previousHref = element.getAttribute('href');
  element.setAttribute('href', href);

  restorers.push(() => {
    if (created) {
      element.remove();
      return;
    }

    if (previousHref === null) {
      element.removeAttribute('href');
      return;
    }

    element.setAttribute('href', previousHref);
  });
};

export const HelmetProvider = ({ children }) => <>{children}</>;

HelmetProvider.propTypes = {
  children: PropTypes.node
};

export const Helmet = ({ children }) => {
  useEffect(() => {
    const restorers = [];

    React.Children.toArray(children).forEach((child) => {
      if (!React.isValidElement(child) || typeof child.type !== 'string') {
        return;
      }

      if (child.type === 'title') {
        const nextTitle = getTextFromChildren(child.props.children);
        const previousTitle = document.title;
        document.title = nextTitle;
        restorers.push(() => {
          document.title = previousTitle;
        });
        return;
      }

      if (child.type === 'meta') {
        upsertMeta(child.props, restorers);
        return;
      }

      if (child.type === 'link') {
        upsertCanonicalLink(child.props, restorers);
      }
    });

    return () => {
      for (let index = restorers.length - 1; index >= 0; index -= 1) {
        restorers[index]();
      }
    };
  }, [children]);

  return null;
};

Helmet.propTypes = {
  children: PropTypes.node
};
